import {
  reorderChannels,
  type DropPosition,
  SignalViewer,
  TherapyDayData,
  TherapyTimeInterval,
} from '@lmt/signal-viewer-ui';
import { useEffect, useState } from 'react';
import { type ParsedEdfInfo } from '@lmt/signal-viewer-processor';
import { getStartOfDayMs } from '../utils/date-helper';
import { DragAndDrop } from './DragAndDrop';
import { FileUploadData } from '../utils/file-upload';
import {
  ChannelsProfileProvider,
  useChannelsProfile,
} from '../contexts/ChannelsProfileContext';
import { normalizeUpload } from '../utils/normalize-upload';
import {
  getBackendEvents,
  getGlobalEventsForGeneration,
} from '../utils/events-helper';
import { useUploadMutation } from './use-upload-mutation';

type UploadSetupResult = {
  therapyData: TherapyDayData;
  profileSwitchId: string;
};

const SeparateWindowChartsViewInner: React.FC = () => {
  const {
    currentProfile,
    hiddenChannelIds,
    switchProfile: onProfileSwitch,
    setChannelOrder,
    setChannelHidden,
  } = useChannelsProfile();
  const [selectedDayMs, setSelectedDayMs] = useState<number | null>(null);
  const [parsedEdfInfo, setParsedEdfInfo] = useState<ParsedEdfInfo[] | null>(
    null,
  );
  const [uploadedTherapyData, setUploadedTherapyData] = useState<
    TherapyDayData | undefined
  >(undefined);

  useEffect(() => {
    if (parsedEdfInfo && parsedEdfInfo[0]) {
      setSelectedDayMs(getStartOfDayMs(parsedEdfInfo[0].startMs));
    }
  }, [parsedEdfInfo]);

  const {
    uploadFiles,
    handleParseProgress,
    completeUploadNotification,
    handleParseErrors,
  } = useUploadMutation<UploadSetupResult>({
    mutationFn: async (uploadedData: FileUploadData, reportProgress) => {
      reportProgress(5);
      const { therapyData, edfManifest, profileSwitchId } =
        await normalizeUpload(uploadedData);
      const backendEvents = await getBackendEvents(uploadedData, edfManifest);
      reportProgress(20);

      return {
        therapyData: backendEvents
          ? { ...therapyData, events: backendEvents }
          : therapyData,
        profileSwitchId,
      };
    },
    onUploadStart: () => {
      setParsedEdfInfo(null);
    },
    onUploadSuccess: (result) => {
      onProfileSwitch(result.profileSwitchId);
      setUploadedTherapyData(result.therapyData);
    },
  });

  const handleUpload = async (uploadedData: FileUploadData) => {
    await uploadFiles(uploadedData);
  };

  const handleReorderChannels = (
    draggedChannelId: string,
    targetChannelId: string,
    position?: DropPosition,
  ) => {
    const nextConfig = reorderChannels(
      currentProfile.config,
      draggedChannelId,
      targetChannelId,
      position,
    );

    setChannelOrder(nextConfig.map((channel) => channel.id));
  };

  const therapyData = uploadedTherapyData;

  const handleViewerSuccess = (data: ParsedEdfInfo[]) => {
    setParsedEdfInfo(data);
    completeUploadNotification(data);
  };

  return (
    <div style={{ flex: '1 1 0', minWidth: 0 }}>
      {therapyData && (
        <SignalViewer
          providedConfigs={currentProfile.config}
          globalEvents={getGlobalEventsForGeneration(therapyData.generation)}
          hiddenChannelIds={hiddenChannelIds}
          onChannelVisibilityToggle={setChannelHidden}
          data={therapyData}
          enableResize={true}
          selectedDayMs={selectedDayMs}
          onSuccess={handleViewerSuccess}
          onParseProgress={handleParseProgress}
          onParseError={handleParseErrors}
          therapyTimeInterval={TherapyTimeInterval.TherapyDay}
          onChannelDelete={() => undefined}
          onChannelsReorder={handleReorderChannels}
        />
      )}

      <DragAndDrop onUpload={handleUpload} />
    </div>
  );
};

export const SeparateWindowChartsView: React.FC = () => (
  <ChannelsProfileProvider>
    <SeparateWindowChartsViewInner />
  </ChannelsProfileProvider>
);
